import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AjaxService } from 'src/app/service/ajax.service';
import { UtilService } from 'src/app/service/util.service';
import { API } from 'src/environments/environment';

interface IdValue {
  id: number;
  text: string;
}

@Component({
  selector: 'app-cadastro-tabela-preco',
  templateUrl: './cadastro-tabela-preco.component.html',
  styleUrls: ['./cadastro-tabela-preco.component.scss']
})
export class CadastroTabelaPrecoComponent implements OnInit {

  formTabelaPreco: FormGroup;
  fornecedores: IdValue[] = [];
  microservicos: IdValue[] = [];
  loading: boolean = false;

  constructor(
    private fb: FormBuilder,
    private ajaxService: AjaxService,
    private util: UtilService
  ) { }

  ngOnInit(): void {
    this.inicializarFormulario();
    this.carregarFiltros();
  }

  inicializarFormulario(): void {
    this.formTabelaPreco = this.fb.group({
      fornecedor: [null, [Validators.required]],
      microservico: [null, [Validators.required]],
      valor: ['', [Validators.required, Validators.min(0.01)]],
      vigenciaInicio: ['', [Validators.required]],
      vigenciaFim: ['', [Validators.required]],
      observacao: ['']
    }, { validators: this.vigenciaValidator });
  }

  vigenciaValidator(group: FormGroup): {[key: string]: any} | null {
    const inicio = group.get('vigenciaInicio')?.value;
    const fim = group.get('vigenciaFim')?.value;
    
    if (inicio && fim) {
      const dataInicio = new Date(inicio);
      const dataFim = new Date(fim);
      
      if (dataInicio >= dataFim) {
        return { 'vigenciaInvalida': true };
      }
    }
    
    return null;
  }

  carregarFiltros(): void {
    const url = API + 'empresa/TabelaValores/ConsultarFiltros';
    this.ajaxService.get(url).subscribe({
      next: (response) => {
        const data = response?.data || {};
        const microservicos = Array.isArray(data.microservicos) ? data.microservicos : [];
        const fornecedores = Array.isArray(data.fornecedores) ? data.fornecedores : [];

        this.microservicos = microservicos
          .map((x: any) => ({ id: Number(x.id || 0), text: this.toText(x.text || x.value) }))
          .filter((x: IdValue) => x.id > 0 && !!x.text);

        this.fornecedores = fornecedores
          .map((x: any) => ({ id: Number(x.id || 0), text: this.toText(x.text || x.value) }))
          .filter((x: IdValue) => x.id > 0 && !!x.text);
      },
      error: () => {
        this.util.toasterShowError('Erro', 'Não foi possível carregar os filtros de fornecedor e microserviço.');
      }
    });
  }

  onSubmit(): void {
    if (this.formTabelaPreco.valid) {
      this.salvar();
    } else {
      this.marcarCamposInvalidos();
    }
  }

  limparFormulario(): void {
    this.formTabelaPreco.reset({
      fornecedor: null,
      microservico: null,
      valor: '',
      vigenciaInicio: '',
      vigenciaFim: '',
      observacao: ''
    });
    this.formTabelaPreco.markAsPristine();
    this.formTabelaPreco.markAsUntouched();
  }

  marcarCamposInvalidos(): void {
    Object.keys(this.formTabelaPreco.controls).forEach(key => {
      const control = this.formTabelaPreco.get(key);
      if (control?.invalid) {
        control.markAsTouched();
      }
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.formTabelaPreco.get(fieldName);
    return field ? field.invalid && field.touched : false;
  }

  getFieldError(fieldName: string): string {
    const field = this.formTabelaPreco.get(fieldName);
    if (field?.errors) {
      if (field.errors['required']) {
        return 'Este campo é obrigatório.';
      }
      if (field.errors['min']) {
        return 'O valor deve ser maior que zero.';
      }
    }
    
    // Verificar erro de vigência
    if (fieldName === 'vigenciaFim' && this.formTabelaPreco.errors?.['vigenciaInvalida']) {
      return 'A data de fim deve ser posterior à data de início.';
    }
    
    return '';
  }

  private salvar(): void {
    const formValue = this.formTabelaPreco.value;
    const payload = {
      ID_FIN_FORNECEDOR: Number(formValue.fornecedor || 0),
      ID_MICROSERVICOS: Number(formValue.microservico || 0),
      VALOR: Number(formValue.valor || 0),
      ID_TIPO_VALOR_FIN: 2, // Valor (R$), conforme tela legada
      INICIO_VIGENCIA: this.toText(formValue.vigenciaInicio),
      FIM_VIGENCIA: this.toText(formValue.vigenciaFim),
      OBSERVACOES: this.toText(formValue.observacao)
    };

    const url = API + 'empresa/TabelaValores/Save';
    this.loading = true;
    this.util.showLoading();
    this.ajaxService.post(url, payload).subscribe({
      next: (response) => {
        this.loading = false;
        this.util.closeLoading();
        if (response?.hasErrors) {
          this.util.toasterShowError('Erro', response?.message || 'Não foi possível salvar a tabela de preço.');
          return;
        }

        this.util.toasterShowSuccess('Sucesso', 'Tabela de preço salva com sucesso!');
        this.limparFormulario();
      },
      error: () => {
        this.loading = false;
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Erro ao salvar a tabela de preço.');
      }
    });
  }

  private toText(value: any): string {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  }
} 